<template>
    <section
        aria-labelledby="snapshot-heading"
        class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
        <header
            class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-gray-200 bg-gray-50 px-5 py-2.5 dark:border-gray-700 dark:bg-gray-900/40"
        >
            <h2
                id="snapshot-heading"
                class="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400"
            >
                Retrato do seller
            </h2>
            <p
                class="font-mono text-[10px] tabular-nums text-gray-400 dark:text-gray-500"
            >
                <template v-if="snapshot">
                    apurado {{ generatedAtLabel }} · cache
                    {{ snapshot.cache_minutes }}min · fonte única com o review
                    de saques
                </template>
                <template v-else-if="error">falha ao carregar</template>
                <template v-else>apurando...</template>
            </p>
        </header>

        <p v-if="error" class="px-5 py-8 text-sm text-rose-500">
            Não foi possível carregar o retrato do seller.
        </p>

        <div
            v-else-if="loading"
            class="grid grid-cols-1 gap-px bg-gray-200 dark:bg-gray-700 sm:grid-cols-2 xl:grid-cols-4"
        >
            <div
                v-for="n in 4"
                :key="n"
                class="space-y-2 bg-white px-5 py-4 dark:bg-gray-800"
            >
                <div
                    class="h-2 w-24 rounded-sm bg-gray-100 dark:bg-gray-700"
                ></div>
                <div
                    class="h-4 w-20 rounded-sm bg-gray-100 dark:bg-gray-700"
                ></div>
                <div
                    class="h-2 w-full rounded-sm bg-gray-100 dark:bg-gray-700"
                ></div>
            </div>
        </div>

        <template v-else-if="snapshot">
            <!-- Os quatro sinais. Separação por 1px de gap (o fundo é a linha),
                 status em pílula, valor em mono grande. -->
            <div
                class="grid grid-cols-1 gap-px bg-gray-200 dark:bg-gray-700 sm:grid-cols-2 xl:grid-cols-4"
            >
                <article
                    v-for="signal in snapshot.signals"
                    :key="signal.key"
                    class="bg-white px-5 py-4 dark:bg-gray-800"
                    :class="signalWash(signal.status)"
                >
                    <div class="flex items-center justify-between gap-2">
                        <h3
                            class="truncate text-[10px] font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                        >
                            {{ signal.label }}
                        </h3>
                        <span
                            class="shrink-0 rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest"
                            :class="signalChip(signal.status)"
                        >
                            {{ statusLabel(signal.status) }}
                        </span>
                    </div>

                    <p
                        class="mt-2 font-mono text-base font-bold tabular-nums tracking-tight"
                        :class="signalValue(signal.status)"
                    >
                        {{ signal.value }}
                    </p>

                    <p
                        class="mt-1.5 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400"
                    >
                        {{ signal.detail }}
                    </p>

                    <p
                        v-if="signal.note"
                        class="mt-1.5 border-l-2 border-gray-200 pl-2 text-[10px] leading-relaxed text-gray-400 dark:border-gray-600 dark:text-gray-500"
                    >
                        {{ signal.note }}
                    </p>
                </article>
            </div>

            <!-- Cada número com escala, ficha e, quando faz sentido, as linhas
                 que o compõem. -->
            <section
                v-for="group in groups"
                :key="group.key"
                class="border-t border-gray-200 px-5 py-4 dark:border-gray-700"
            >
                <div class="mb-3 flex items-center gap-2">
                    <span
                        aria-hidden="true"
                        class="h-3 w-0.5 rounded-full bg-gray-300 dark:bg-gray-600"
                    ></span>
                    <h3
                        class="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500"
                    >
                        {{ group.label }}
                    </h3>
                </div>

                <div class="grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-3">
                    <article
                        v-for="metric in metricsOf(group.key)"
                        :key="metric.key"
                    >
                        <div class="flex items-baseline justify-between gap-3">
                            <h4
                                class="text-[11px] text-gray-500 dark:text-gray-400"
                            >
                                {{ metric.label }}
                            </h4>
                            <div class="flex shrink-0 items-baseline gap-3">
                                <button
                                    v-if="metric.evidence_count > 0"
                                    type="button"
                                    class="text-[9px] uppercase tracking-[0.12em] text-indigo-500 transition-colors hover:text-indigo-400 focus:outline-none dark:text-indigo-400"
                                    :aria-expanded="openEvidence === metric.key"
                                    @click="toggleEvidence(metric)"
                                >
                                    {{
                                        openEvidence === metric.key
                                            ? "ocultar"
                                            : `ver as ${metric.evidence_count}`
                                    }}
                                </button>
                                <button
                                    type="button"
                                    class="text-[9px] uppercase tracking-[0.12em] text-gray-400 transition-colors hover:text-indigo-500 focus:outline-none dark:text-gray-500 dark:hover:text-indigo-400"
                                    :aria-expanded="isOpen(metric.key)"
                                    @click="toggle(metric.key)"
                                >
                                    {{
                                        isOpen(metric.key) ? "fechar" : "ficha"
                                    }}
                                </button>
                            </div>
                        </div>

                        <p
                            class="mt-0.5 font-mono text-lg font-semibold tabular-nums tracking-tight text-gray-900 dark:text-white"
                        >
                            {{ metric.value }}
                        </p>

                        <!-- Escala: onde o número está em relação ao limiar que
                             decide. O traço vertical é o limiar. -->
                        <div
                            v-if="metric.scales && metric.scales.length"
                            class="mt-2 space-y-1"
                        >
                            <div
                                v-for="(scale, index) in metric.scales"
                                :key="index"
                                class="flex items-center gap-2"
                            >
                                <span
                                    v-if="scale.label"
                                    class="w-12 shrink-0 font-mono text-[9px] uppercase tracking-wide text-gray-400 dark:text-gray-500"
                                >
                                    {{ scale.label }}
                                </span>
                                <div
                                    class="relative h-1 flex-1 rounded-sm bg-gray-100 dark:bg-gray-700"
                                >
                                    <div
                                        class="absolute inset-y-0 left-0 rounded-sm"
                                        :class="scaleFill(scale.status)"
                                        :style="{ width: scale.pct + '%' }"
                                    ></div>
                                    <span
                                        v-if="scale.threshold_pct !== null"
                                        class="absolute -bottom-0.5 -top-0.5 w-px bg-gray-400 dark:bg-gray-400"
                                        :style="{
                                            left: scale.threshold_pct + '%',
                                        }"
                                        :title="`limiar ${scale.threshold}`"
                                    ></span>
                                </div>
                            </div>
                        </div>

                        <!-- Ritmo de venda: um slot por dia, vão é dia parado.
                             A faixa transparente por cima garante alvo de hover
                             também nos dias sem venda, que têm altura zero. -->
                        <div
                            v-if="metric.series && metric.series.length"
                            class="mt-2"
                        >
                            <svg
                                class="h-7 w-full text-indigo-400 dark:text-indigo-400/80"
                                :viewBox="`0 0 ${metric.series.length * 2} 20`"
                                preserveAspectRatio="none"
                                role="img"
                                :aria-label="`Vendas por dia nos últimos ${metric.series.length} dias`"
                                @mouseleave="hoveredDay = null"
                            >
                                <rect
                                    v-for="(point, index) in metric.series"
                                    :key="`base-${point.date}`"
                                    :x="index * 2"
                                    y="19"
                                    width="1.2"
                                    height="1"
                                    class="fill-gray-200 dark:fill-gray-600"
                                />
                                <rect
                                    v-for="(point, index) in metric.series"
                                    :key="`bar-${point.date}`"
                                    :x="index * 2"
                                    :y="
                                        20 -
                                        barHeight(point.value, metric.series)
                                    "
                                    width="1.2"
                                    :height="
                                        barHeight(point.value, metric.series)
                                    "
                                    class="fill-current"
                                    :class="{
                                        'opacity-40':
                                            hoveredDay &&
                                            hoveredDay.date !== point.date,
                                    }"
                                />
                                <rect
                                    v-for="(point, index) in metric.series"
                                    :key="`hit-${point.date}`"
                                    :x="index * 2 - 0.4"
                                    y="0"
                                    width="2"
                                    height="20"
                                    fill="transparent"
                                    @mouseenter="hoveredDay = point"
                                />
                            </svg>

                            <p
                                class="mt-1 h-3 font-mono text-[10px] tabular-nums text-gray-500 dark:text-gray-400"
                                aria-live="polite"
                            >
                                <template v-if="hoveredDay">
                                    {{ dayLabel(hoveredDay.date) }} ·
                                    {{ hoveredDay.value }}
                                    {{
                                        hoveredDay.value === 1
                                            ? "venda paga"
                                            : "vendas pagas"
                                    }}
                                </template>
                            </p>
                        </div>

                        <p
                            v-if="metric.numerator"
                            class="mt-2 font-mono text-[10px] tabular-nums leading-relaxed text-gray-500 dark:text-gray-400"
                        >
                            <span class="text-gray-700 dark:text-gray-200">
                                {{ metric.numerator.value }}
                            </span>
                            {{ metric.numerator.label }}
                            <template v-if="metric.denominator">
                                <span class="text-gray-400 dark:text-gray-600">
                                    ÷
                                </span>
                                <span class="text-gray-700 dark:text-gray-200">
                                    {{ metric.denominator.value }}
                                </span>
                                {{ metric.denominator.label }}
                            </template>
                        </p>

                        <p
                            v-if="metric.breakdown"
                            class="mt-0.5 text-[10px] leading-relaxed text-gray-400 dark:text-gray-500"
                        >
                            {{ metric.breakdown }}
                        </p>

                        <dl
                            v-if="isOpen(metric.key)"
                            class="mt-3 divide-y divide-gray-100 border-y border-gray-200 dark:divide-gray-700/60 dark:border-gray-700"
                        >
                            <div
                                v-for="row in fichaRows(metric)"
                                :key="row.label"
                                class="grid grid-cols-[5.5rem_1fr] gap-3 py-1.5"
                            >
                                <dt
                                    class="text-[9px] uppercase tracking-widest text-gray-400 dark:text-gray-500"
                                >
                                    {{ row.label }}
                                </dt>
                                <dd
                                    class="text-[11px] leading-relaxed text-gray-600 dark:text-gray-300"
                                >
                                    {{ row.value }}
                                </dd>
                            </div>
                        </dl>
                    </article>
                </div>

                <!-- As linhas por trás do número: o que substitui conferir a
                     conta com alguém. -->
                <div
                    v-if="evidenceBelongsTo(group.key)"
                    class="mt-4 border-t border-gray-200 pt-3 dark:border-gray-700"
                >
                    <div
                        class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1"
                    >
                        <h4
                            class="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400"
                        >
                            {{ evidence ? evidence.label : "carregando" }}
                        </h4>
                        <button
                            v-if="evidence && evidence.rows.length"
                            type="button"
                            class="text-[9px] uppercase tracking-[0.12em] text-indigo-500 hover:text-indigo-400 focus:outline-none dark:text-indigo-400"
                            @click="downloadCsv"
                        >
                            baixar csv
                        </button>
                    </div>

                    <p
                        v-if="evidence"
                        class="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500"
                    >
                        {{ evidence.description }} ·
                        {{ evidence.returned }} linha(s) ·
                        {{ evidenceTotalLabel }}
                        <span v-if="evidence.truncated" class="text-amber-500">
                            · lista truncada em
                            {{ evidence.max_rows }}, exporte o CSV para o
                            conjunto completo
                        </span>
                    </p>

                    <p
                        v-if="evidenceError"
                        class="mt-3 text-[11px] text-rose-500"
                    >
                        Não foi possível carregar as linhas.
                    </p>

                    <p
                        v-else-if="evidenceLoading"
                        class="mt-3 font-mono text-[11px] text-gray-400 dark:text-gray-500"
                    >
                        buscando...
                    </p>

                    <div v-else-if="evidence" class="mt-2 overflow-x-auto">
                        <table class="w-full min-w-160 text-left">
                            <thead>
                                <tr
                                    class="border-b border-gray-200 text-[9px] uppercase tracking-widest text-gray-400 dark:border-gray-700 dark:text-gray-500"
                                >
                                    <th class="py-1.5 pr-4 font-medium">
                                        transação
                                    </th>
                                    <th class="py-1.5 pr-4 font-medium">
                                        venda
                                    </th>
                                    <th class="py-1.5 pr-4 font-medium">
                                        {{ evidence.event_label }}
                                    </th>
                                    <th class="py-1.5 pr-4 font-medium">
                                        método
                                    </th>
                                    <th class="py-1.5 pr-4 font-medium">
                                        status
                                    </th>
                                    <th class="py-1.5 text-right font-medium">
                                        comissão
                                    </th>
                                </tr>
                            </thead>
                            <tbody
                                class="divide-y divide-gray-100 dark:divide-gray-700/60"
                            >
                                <tr
                                    v-for="row in evidence.rows"
                                    :key="row.hash"
                                    class="font-mono text-[10px] tabular-nums text-gray-600 dark:text-gray-300"
                                >
                                    <td class="py-1.5 pr-4">{{ row.hash }}</td>
                                    <td class="py-1.5 pr-4">
                                        {{ row.sale_at }}
                                    </td>
                                    <td class="py-1.5 pr-4">
                                        {{ row.event_at || "-" }}
                                    </td>
                                    <td
                                        class="py-1.5 pr-4 text-gray-400 dark:text-gray-500"
                                    >
                                        {{ row.payment_method }}
                                    </td>
                                    <td
                                        class="py-1.5 pr-4 text-gray-400 dark:text-gray-500"
                                    >
                                        {{ row.status }}
                                    </td>
                                    <td class="py-1.5 text-right">
                                        {{ row.amount_label }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </template>
    </section>
</template>

<script>
export default {
    props: {
        user: { required: true },
    },

    data() {
        return {
            loading: true,
            error: false,
            snapshot: null,
            openFichas: [],
            hoveredDay: null,
            openEvidence: null,
            evidence: null,
            evidenceLoading: false,
            evidenceError: false,
            groups: [
                { key: "chargeback", label: "Disputa de cartão" },
                { key: "refund", label: "Reembolso" },
                { key: "sales", label: "Venda e saída" },
                { key: "exposure", label: "Exposição e cobertura" },
            ],
        };
    },

    computed: {
        generatedAtLabel() {
            if (!this.snapshot) {
                return "";
            }

            return new Date(this.snapshot.generated_at).toLocaleTimeString(
                "pt-BR",
                { hour: "2-digit", minute: "2-digit" },
            );
        },

        evidenceTotalLabel() {
            if (!this.evidence) {
                return "";
            }

            if (typeof this.evidence.total_amount !== "number") {
                return `${this.evidence.total_amount} em comissão`;
            }

            return `${(this.evidence.total_amount / 100).toLocaleString(
                "pt-BR",
                { style: "currency", currency: "BRL" },
            )} em comissão`;
        },
    },

    watch: {
        user() {
            this.fetch();
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        userId() {
            return this.$route?.params?.user_id ?? this.user.id;
        },

        fetch() {
            this.loading = true;
            this.error = false;
            this.openEvidence = null;
            this.evidence = null;

            axios
                .get(`/api/user/${this.userId()}/metrics/snapshot`)
                .then(({ data }) => {
                    this.snapshot = data;
                    this.$emit("data-loaded", data);
                })
                .catch(() => {
                    this.error = true;
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        metricsOf(group) {
            return (this.snapshot?.metrics ?? []).filter(
                (metric) => metric.group === group,
            );
        },

        evidenceBelongsTo(group) {
            if (!this.openEvidence) {
                return false;
            }

            return this.metricsOf(group).some(
                (metric) => metric.key === this.openEvidence,
            );
        },

        toggleEvidence(metric) {
            if (this.openEvidence === metric.key) {
                this.openEvidence = null;
                this.evidence = null;

                return;
            }

            this.openEvidence = metric.key;
            this.evidence = null;
            this.evidenceError = false;
            this.evidenceLoading = true;

            axios
                .get(`/api/user/${this.userId()}/metrics/evidence`, {
                    params: { metric: metric.key },
                })
                .then(({ data }) => {
                    // A resposta pode chegar depois de o reviewer fechar ou
                    // trocar de métrica: só publica se ainda for a atual.
                    if (this.openEvidence === data.metric) {
                        this.evidence = data;
                    }
                })
                .catch(() => {
                    this.evidenceError = true;
                })
                .finally(() => {
                    this.evidenceLoading = false;
                });
        },

        downloadCsv() {
            if (!this.evidence) {
                return;
            }

            const header = [
                "transacao",
                "venda",
                this.evidence.event_label,
                "metodo",
                "status",
                "comissao",
            ];

            // amount_label já vem mascarado para quem não tem permissão
            // financeira: o CSV não pode ser a porta dos fundos do valor.
            const lines = this.evidence.rows.map((row) => [
                row.hash,
                row.sale_at,
                row.event_at || "",
                row.payment_method,
                row.status,
                row.amount_label,
            ]);

            const csv = [header, ...lines]
                .map((columns) =>
                    columns.map((value) => `"${value}"`).join(";"),
                )
                .join("\n");

            // BOM para o Excel pt-BR abrir com acento e separador corretos.
            const blob = new Blob([`﻿${csv}`], {
                type: "text/csv;charset=utf-8;",
            });
            const link = document.createElement("a");

            link.href = URL.createObjectURL(blob);
            link.download = `${this.evidence.metric}-user-${this.userId()}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
        },

        dayLabel(date) {
            // A série vem em ISO (Y-m-d); Date com string ISO pura é UTC e
            // devolveria o dia anterior no fuso do Brasil.
            const [year, month, day] = date.split("-");

            return `${day}/${month}/${year}`;
        },

        barHeight(value, series) {
            if (!value) {
                return 0;
            }

            const peak = Math.max(...series.map((point) => point.value), 1);

            return Math.max(3, Math.round((value / peak) * 19));
        },

        fichaRows(metric) {
            return [
                { label: "fórmula", value: metric.formula },
                { label: "janela", value: metric.window },
                { label: "status", value: metric.statuses },
                { label: "métodos", value: metric.methods },
                { label: "base de data", value: metric.date_basis },
                { label: "fonte", value: metric.source },
            ];
        },

        isOpen(key) {
            return this.openFichas.includes(key);
        },

        toggle(key) {
            this.openFichas = this.isOpen(key)
                ? this.openFichas.filter((item) => item !== key)
                : [...this.openFichas, key];
        },

        scaleFill(status) {
            return {
                ok: "bg-emerald-500/70",
                attention: "bg-amber-500/80",
                critical: "bg-rose-500/80",
                neutral: "bg-indigo-400/70",
                muted: "bg-gray-300 dark:bg-gray-600",
            }[status];
        },

        statusLabel(status) {
            return {
                ok: "ok",
                attention: "atenção",
                critical: "alerta",
                neutral: "sem base",
            }[status];
        },

        signalChip(status) {
            return {
                ok: "bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300",
                attention:
                    "bg-amber-500/10 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300",
                critical:
                    "bg-rose-500/10 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300",
                neutral: "bg-gray-500/10 text-gray-500 dark:text-gray-400",
            }[status];
        },

        signalValue(status) {
            return {
                ok: "text-emerald-600 dark:text-emerald-400",
                attention: "text-amber-600 dark:text-amber-400",
                critical: "text-rose-600 dark:text-rose-400",
                neutral: "text-gray-600 dark:text-gray-300",
            }[status];
        },

        signalWash(status) {
            return {
                ok: "",
                attention: "bg-amber-50/70! dark:bg-amber-500/4!",
                critical: "bg-rose-50/70! dark:bg-rose-500/5!",
                neutral: "",
            }[status];
        },
    },
};
</script>
